import {Fizz, Hallelujah, Music, PlaceInEmptyTub, TubDrain, TubFill, TubOverdrain, TubOverfill} from "./sounds";

export type BombColor = "Red" | "Yellow" | "Blue";
type TubItem = "RedBomb" | "YellowBomb" | "BlueBomb" | "Surprise";

function toTubItem(bombColor: BombColor): TubItem
{
    switch (bombColor) {
        case "Red":
            return "RedBomb";
        case "Yellow":
            return "YellowBomb";
        case "Blue":
            return "BlueBomb";
    }
}

type Color = [number, number, number];

function rgbComponentToHex(rgb)
{
    let hex = Number(rgb).toString(16);
    if (hex.length < 2) {
        hex = "0" + hex;
    }
    return hex;
}

function toHex(color: Color)
{
    const red = rgbComponentToHex(Math.floor(color[0] * 255));
    const green = rgbComponentToHex(Math.floor(color[1] * 255));
    const blue = rgbComponentToHex(Math.floor(color[2] * 255));
    return red+green+blue;
}

function lerpNumber(a: number, b: number, factor: number)
{
    return a*(1-factor)+b*factor;
}

function lerpColor(a: Color, b: Color, factor: number): Color
{
    return [lerpNumber(a[0], b[0], factor), lerpNumber(a[1], b[1], factor), lerpNumber(a[2], b[2], factor)];
}

function getColor(bombColor: BombColor): Color
{
    switch (bombColor) {
        case "Red":
            return [1, 0, 0];
        case "Yellow":
            return [1, 1, 0];
        case "Blue":
            return [0, 0, 1];
    }
}

function getColorFromTubItem(tubItem: "RedBomb" | "YellowBomb" | "BlueBomb"): Color
{
    switch (tubItem) {
        case "RedBomb":
            return getColor("Red");
        case "YellowBomb":
            return getColor("Yellow");
        case "BlueBomb":
            return getColor("Blue");
    }
}

const emptyColor: Color = [0.8, 0.8, 0.8];
const defaultWaterColor: Color = [0, 0.7, 0.9];

interface GameState {
    tubItems: TubItem[],
    waterInTub: boolean,
    waterColor: Color,
    secretsCollected: number,
    started: boolean,
    overfilledTimesCount: number
}

const initialGameState: GameState = {
    tubItems: [],
    waterColor: [0, 0, 0],
    waterInTub: false,
    secretsCollected: 0,
    started: false,
    overfilledTimesCount: 0
};

export const Game = {
    _gameState: { ...initialGameState },
    get isStarted()
    {
        return this._gameState.started;
    },
    start: function()
    {
        if (this.isStarted)
            return;

        this._gameState.started = true;
        Music.loop(true).play();
    },
    fillTub: function ()
    {
        if (this._gameState.waterInTub)
        {
            TubDrain.stop();
            TubOverfill.play();
            this._gameState.waterColor = lerpColor(this._gameState.waterColor, defaultWaterColor, 0.3);
            this._gameState.overfilledTimesCount++;
            return;
        }
        TubFill.play();
        this._gameState.waterInTub = true;
        this._gameState.waterColor = defaultWaterColor;

        const previousTubItemsLength = this._gameState.tubItems.length;
        this._gameState.tubItems = this._gameState.tubItems.reduce((accumulator, tubItem) => {
            if (tubItem === "Surprise")
            {
                accumulator.push(tubItem);
                return accumulator;
            }

            this._gameState.waterColor = lerpColor(this._gameState.waterColor, getColorFromTubItem(tubItem), 0.6);
            if (tubItem === "YellowBomb")
                accumulator.push("Surprise");

            return accumulator;
        }, []);

        if (this._gameState.tubItems.length !== previousTubItemsLength)
            Fizz.play();
    },
    drainTub: function ()
    {
        TubFill.stop();
        TubOverfill.stop();
        if (!this._gameState.waterInTub)
        {
            TubOverdrain.play();
            return;
        }
        TubDrain.play();
        this._gameState.waterInTub = false;
    },
    addBomb: function (bombColor: BombColor)
    {
        if (this._gameState.waterInTub)
        {
            Fizz.play();
            const color = getColor(bombColor);
            this._gameState.waterColor = lerpColor(this._gameState.waterColor, color, 0.2);

            if (bombColor === "Yellow")
                this._gameState.tubItems.push("Surprise");
        }
        else
        {
            this._gameState.tubItems.push(toTubItem(bombColor));
            PlaceInEmptyTub.play();
        }
    },
    removeTopmostTubItem: function ()
    {
        if (!this.hasTubItems)
            return;

        const tubItem = this._gameState.tubItems.pop();
        if (tubItem === "Surprise")
        {
            Hallelujah.stop().play();
            this._gameState.secretsCollected++;
        }
        else
        {
            // TODO sound
        }
    },
    get topmostTubItem()
    {
        if (!this.hasTubItems)
            return undefined;

        return this._gameState.tubItems[this._gameState.tubItems.length - 1];
    },
    get hasTubItems()
    {
        return this._gameState.tubItems.length > 0;
    },
    get tubColor()
    {
        if (!this._gameState.waterInTub)
            return emptyColor;
        return this._gameState.waterColor;
    },
    get tubColorHex()
    {
        return toHex(this.tubColor);
    },
    get isTubFilled()
    {
        return this._gameState.waterInTub;
    },
    get numberOfTubItems()
    {
        return this._gameState.tubItems.length;
    }
};